import React from 'react';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const TechnicianPerformanceTable = ({ technicians, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/3"></div>
          {[1, 2, 3, 4]?.map(i => (
            <div key={i} className="h-16 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const getEfficiencyColor = (efficiency) => {
    if (efficiency >= 90) return 'text-success';
    if (efficiency >= 75) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-muted/50 px-6 py-4 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          Desempenho dos Técnicos
        </h2>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Técnico
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Tarefas Concluídas
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Projetos Ativos
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Avaliação
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Eficiência
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Tempo Resposta
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {technicians?.map((tech) => (
              <tr key={tech?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <img
                      src={tech?.avatar}
                      alt={`Avatar de ${tech?.name}`}
                      className="w-10 h-10 rounded-full"
                    />
                    <div className="text-sm font-medium text-foreground">
                      {tech?.name}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground font-medium">
                    {tech?.completedTasks}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">
                    {tech?.activeProjects}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} className="text-warning fill-warning" />
                    <span className="text-sm font-medium text-foreground">
                      {tech?.averageRating}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-muted rounded-full h-2 max-w-[100px]">
                      <div
                        className={cn(
                          "h-2 rounded-full transition-all",
                          tech?.efficiency >= 90 ? 'bg-success' : 
                          tech?.efficiency >= 75 ? 'bg-warning' : 'bg-error'
                        )}
                        style={{ width: `${tech?.efficiency}%` }}
                      ></div>
                    </div>
                    <span className={cn(
                      "text-sm font-medium",
                      getEfficiencyColor(tech?.efficiency)
                    )}>
                      {tech?.efficiency}%
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-foreground">
                    {tech?.responseTime}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-border">
        {technicians?.map((tech) => (
          <div key={tech?.id} className="p-4">
            <div className="flex items-center space-x-3 mb-4">
              <img
                src={tech?.avatar}
                alt={`Avatar de ${tech?.name}`}
                className="w-12 h-12 rounded-full"
              />
              <div className="flex-1">
                <div className="font-medium text-foreground mb-1">
                  {tech?.name}
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-warning fill-warning" />
                  <span className="text-sm text-foreground">
                    {tech?.averageRating}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-muted-foreground mb-1">Tarefas</div>
                <div className="font-medium text-foreground">
                  {tech?.completedTasks}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Projetos</div>
                <div className="font-medium text-foreground">
                  {tech?.activeProjects}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Eficiência</div>
                <div className={cn(
                  "font-medium",
                  getEfficiencyColor(tech?.efficiency)
                )}>
                  {tech?.efficiency}%
                </div>
              </div>
              <div>
                <div className="text-muted-foreground mb-1">Resposta</div>
                <div className="font-medium text-foreground">
                  {tech?.responseTime}
                </div>
              </div>
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                <span>Desempenho</span>
                <span>{tech?.efficiency}%</span>
              </div>
              <div className="bg-muted rounded-full h-2">
                <div
                  className={cn(
                    "h-2 rounded-full transition-all",
                    tech?.efficiency >= 90 ? 'bg-success' : 
                    tech?.efficiency >= 75 ? 'bg-warning' : 'bg-error'
                  )}
                  style={{ width: `${tech?.efficiency}%` }}
                ></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TechnicianPerformanceTable;